<template>
  <div class="letter-hunt">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <h1
        ref="dropZoneRef"
        :class="['target-letter', { 'correct-drop': isCorrectDrop }]"
        @dragover.prevent
        @drop="(e) => handleDrop(e, $refs.dropZoneRef)"
        @click="speakOutLoud(targetLetter.toLowerCase())"
      >
        <template v-if="selectedCategory === 'numbers'">
          <div class="dot-container">
            <span v-for="n in numDots" :key="n" class="dot" />
          </div>
        </template>
        <template v-else>
          {{ targetLetter }}
        </template>
      </h1>
      <p v-if="message" class="message">{{ message }}</p>
      <div class="grid">
        <div
          v-for="(letter, index) in shuffledLetters"
          :key="index"
          class="letter-draggable"
          :class="{ 'being-dragged': dragHelper.visible && dragHelper.letter === letter }"
          draggable="true"
          @dragstart="(e) => handleDragStart(e, letter)"
          @click="speakOutLoud(letter.toLowerCase())"
          @touchstart.prevent="(e) => handleTouchStart(e, letter)"
          @touchmove.prevent="handleTouchMove"
          @touchend.prevent="(e) => handleTouchEnd(e, $refs.dropZoneRef)"
        >
          {{ letter }}
        </div>
      </div>

      <div
        v-if="dragHelper.visible"
        class="drag-helper"
        :style="{
          position: 'fixed',
          left: dragHelper.x + 'px',
          top: dragHelper.y + 'px',
          transform: 'translate(-50%, -50%)',
          'z-index': 1000,
        }"
      >
        {{ dragHelper.letter }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useDragAndDrop } from '@/composables/useDragAndDrop';
import { useSpeechSynthesis } from '@/composables/useSpeechSynthesis';
import LevelComplete from '../LevelComplete.vue';

export default {
  name: 'LetterHunt',
  components: {
    LevelComplete,
  },
  props: {
    selectedCategory: {
      type: String,
      default: 'vowels',
    },
  },
  setup(props) {
    const { speakOutLoud, voicesLoaded } = useSpeechSynthesis();

    const categories = {
      vowels: ['A', 'E', 'I', 'O', 'U'],
      alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
      numbers: '123456789'.split(''),
    };

    const letters = ref([]);
    const targetLetter = ref('');
    const message = ref('');
    const isCorrectDrop = ref(false);
    const letterCounts = ref({});
    const maxCount = 1;
    const isGameComplete = ref(false);
    const dropZoneRef = ref(null);

    const currentCategory = computed(() => categories[props.selectedCategory]);

    const shuffledLetters = computed(() =>
      [...letters.value].sort(() => Math.random() - 0.5)
    );

    const numDots = computed(() => {
      const num = parseInt(targetLetter.value);
      return Number.isNaN(num) || num < 1 ? 0 : num;
    });

    // Track all active timeouts so they can be cleared on unmount
    const timeouts = [];

    const initializeLetterCounts = () => {
      letterCounts.value = currentCategory.value.reduce((acc, letter) => {
        acc[letter] = 0;
        return acc;
      }, {});
    };

    const playSound = (url) => new Audio(url).play();

    const setupGame = () => {
      const availableLetters = currentCategory.value.filter(
        (letter) => letterCounts.value[letter] < maxCount
      );

      if (availableLetters.length === 0) {
        playSound(
          'https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3'
        );
        speakOutLoud('Great job!');
        isGameComplete.value = true;

        const timeoutId = setTimeout(() => {
          window.location.href = `/${
            props.selectedCategory === 'numbers' ? 'numbers' : 'letters'
          }`;
        }, 4000);
        timeouts.push(timeoutId);
        return;
      }

      targetLetter.value =
        availableLetters[Math.floor(Math.random() * availableLetters.length)];

      speakOutLoud(
        `Find the ${
          props.selectedCategory === 'numbers' ? 'number' : 'letter'
        } "${targetLetter.value}"`
      );

      const otherLetters = currentCategory.value.filter(
        (l) => l !== targetLetter.value
      );
      if (otherLetters.length === 0) otherLetters.push(targetLetter.value);

      letters.value = Array.from({ length: 11 }, () =>
        otherLetters[Math.floor(Math.random() * otherLetters.length)]
      );

      const randomIndex = Math.floor(Math.random() * (letters.value.length + 1));
      letters.value.splice(randomIndex, 0, targetLetter.value);

      message.value = '';
      isCorrectDrop.value = false;
    };

    const handleDropLogic = ({ valid }) => {
      if (valid) {
        playSound(
          'https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a'
        );
        speakOutLoud(targetLetter.value.toLowerCase());
        message.value = '✅';
        isCorrectDrop.value = true;
        letterCounts.value[targetLetter.value]++;
        const timeoutId = setTimeout(() => {
          setupGame();
        }, 3000);
        timeouts.push(timeoutId);
      } else {
        message.value = '❌';
        const timeoutId = setTimeout(() => {
          message.value = '';
        }, 3000);
        timeouts.push(timeoutId);
      }
    };

    const {
      dragHelper,
      handleDragStart,
      handleDrop,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    } = useDragAndDrop({
      onDrop: handleDropLogic,
      isValidDropTarget: (data) => data === targetLetter.value,
    });

    onMounted(() => {
      initializeLetterCounts();
      setupGame();
    });

    onBeforeUnmount(() => {
      timeouts.forEach(clearTimeout);
      timeouts.length = 0;
    });

    return {
      dropZoneRef,
      letters,
      targetLetter,
      message,
      isCorrectDrop,
      isGameComplete,
      dragHelper,
      shuffledLetters,
      numDots,
      handleDragStart,
      handleDrop,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      speakOutLoud,
      voicesLoaded,
    };
  },
};
</script>

<style scoped>
.letter-hunt {
  position: relative;
  user-select: none;
}
.grid {
  max-width: 500px;
  margin: auto;
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
}
.target-letter {
  max-width: 100px;
  margin: auto;
  margin-bottom: 20px;
  font-size: 4rem;
  color: #ff4081;
  text-align: center;
  background-color: lightgray;
  border-radius: 10px;
  border: 8px dashed gray;
  cursor: pointer;
  transition: all 0.3s;
}
.correct-drop {
  background-color: lightgreen;
  transform: scale(1.2);
  border: 8px green solid;
}
.letter-draggable {
  cursor: pointer;
  border-radius: 10px;
  padding: 20px;
  font-size: 2rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
  user-select: none;
  touch-action: none;
}
.letter-draggable:hover {
  transform: scale(1.1);
}
.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}
.dot-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: minmax(20px, 1fr);
  width: 60px;
  height: 60px;
  margin: auto;
  align-items: center;
  justify-items: center;
}
.dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: black;
}
.drag-helper {
  position: fixed;
  pointer-events: none;
  border-radius: 10px;
  padding: 20px;
  font-size: 2rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
  user-select: none;
  touch-action: none;
}
.being-dragged {
  opacity: 0.5;
}
</style>
